import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconAlertTriangle, IconRefresh, IconTrash } from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

interface DeliveryRow {
  id: string;
  vendorId: number;
  eventType: string;
  endpoint: string;
  payloadJson: string;
  status: string;
  attemptCount: number;
  lastAttemptAt: Date | string | null;
  lastStatusCode: number | null;
  lastError: string | null;
  nextAttemptAt: Date | string | null;
  createdAt: Date | string;
}

type EffectiveStatus = 'queued' | 'retrying' | 'delivered' | 'dead';

const STATUS_BADGE: Record<EffectiveStatus, { color: string; label: string }> = {
  queued: { color: 'gray', label: 'queued' },
  retrying: { color: 'orange', label: 'retrying' },
  delivered: { color: 'green', label: 'delivered' },
  dead: { color: 'red', label: 'dead' },
};

/** The stored row stays status='pending' between retries; surface the honest
 *  state — a delivery that has already failed at least once is "retrying", not
 *  "pending" (and a never-attempted row is "queued"). */
function effectiveStatus(d: DeliveryRow): EffectiveStatus {
  if (d.status === 'delivered') return 'delivered';
  if (d.status === 'dead') return 'dead';
  return d.attemptCount > 0 ? 'retrying' : 'queued';
}

const FILTERS = ['all', 'queued', 'retrying', 'delivered', 'dead'] as const;
type StatusFilter = (typeof FILTERS)[number];

export function DeliveryLogPanel() {
  const utils = trpc.useUtils();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [confirmClear, confirmClearHandlers] = useDisclosure(false);

  const deliveries = trpc.tenancy.outgoingWebhook.deliveries.useQuery({
    status: statusFilter === 'all' ? undefined : statusFilter,
    take: 50,
  });

  const replay = trpc.tenancy.outgoingWebhook.replay.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Re-queued for delivery', color: 'green' });
      void utils.tenancy.outgoingWebhook.deliveries.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const replayDead = trpc.tenancy.outgoingWebhook.replayDeadBatch.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: `Re-queued ${r.requeued} dead deliver${r.requeued === 1 ? 'y' : 'ies'}`,
        color: 'green',
      });
      void utils.tenancy.outgoingWebhook.deliveries.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const clearLog = trpc.tenancy.outgoingWebhook.clearDeliveries.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: `Cleared ${r.cleared} log row${r.cleared === 1 ? '' : 's'}`,
        color: 'green',
      });
      confirmClearHandlers.close();
      void utils.tenancy.outgoingWebhook.deliveries.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  if (deliveries.isPending) return <Skeleton height={400} />;
  if (deliveries.isError) {
    return (
      <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
        {deliveries.error.message}
      </Alert>
    );
  }

  const items = (deliveries.data?.items ?? []) as DeliveryRow[];
  // Surface a receiver-health warning from the most recent failing delivery.
  const failing = items.find(
    (d) =>
      (effectiveStatus(d) === 'retrying' || effectiveStatus(d) === 'dead') &&
      ((d.lastStatusCode != null && d.lastStatusCode >= 400) || !!d.lastError),
  );

  return (
    <Stack gap="md">
      <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
        Every outbound webhook attempt is logged here for 30 days. Transient failures retry
        automatically with exponential backoff (~46h window); permanent errors (e.g. a 404 from your
        receiver) stop after a couple of attempts. Once a delivery is marked <b>dead</b> you can
        manually replay it after your receiver is back online.
      </Alert>

      {failing && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            Forwards to your receiver are failing
            {failing.lastStatusCode ? ` (HTTP ${failing.lastStatusCode})` : ''}. Check the endpoint
            URL under <b>Outgoing webhooks</b>, or turn the feature off.
          </Text>
          <Text size="xs" c="dimmed" mt={4} ff="monospace" style={{ wordBreak: 'break-all' }}>
            {failing.endpoint}
          </Text>
        </Alert>
      )}

      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            Status:
          </Text>
          {FILTERS.map((s) => (
            <Button
              key={s}
              size="xs"
              variant={statusFilter === s ? 'filled' : 'subtle'}
              onClick={() => setStatusFilter(s)}
            >
              {s}
            </Button>
          ))}
        </Group>
        <Group gap="xs" wrap="nowrap">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconRefresh size={14} />}
            loading={replayDead.isPending}
            onClick={() => replayDead.mutate({})}
          >
            Replay all dead
          </Button>
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconTrash size={14} />}
            disabled={items.length === 0}
            onClick={confirmClearHandlers.open}
          >
            Clear log
          </Button>
        </Group>
      </Group>

      <Stack gap={4}>
        {items.length === 0 && (
          <Text c="dimmed" ta="center" py="xl" size="sm">
            No deliveries yet.
          </Text>
        )}
        {items.map((d) => {
          const isOpen = expanded === d.id;
          const eff = effectiveStatus(d);
          const badge = STATUS_BADGE[eff];
          return (
            <Card key={d.id} withBorder p="sm">
              <Group
                justify="space-between"
                wrap="nowrap"
                style={{ cursor: 'pointer' }}
                onClick={() => setExpanded(isOpen ? null : d.id)}
              >
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                  <Badge color={badge.color} variant="light" size="sm">
                    {badge.label}
                  </Badge>
                  <Text size="sm" fw={500} ff="monospace">
                    {d.eventType}
                  </Text>
                  {d.lastStatusCode && (
                    <Text size="xs" c="dimmed">
                      HTTP {d.lastStatusCode}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed">
                    attempt {d.attemptCount}
                  </Text>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  <Text size="xs" c="dimmed">
                    {new Date(d.createdAt).toLocaleString()}
                  </Text>
                  {eff === 'dead' && (
                    <Tooltip label="Re-queue for delivery">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        loading={replay.isPending && replay.variables?.deliveryId === d.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          replay.mutate({ deliveryId: d.id });
                        }}
                      >
                        <IconRefresh size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Group>
              {isOpen && (
                <Stack
                  gap={6}
                  mt="sm"
                  pl="sm"
                  style={{ borderLeft: '2px solid var(--mantine-color-gray-3)' }}
                >
                  <Text size="xs" c="dimmed" ff="monospace" style={{ wordBreak: 'break-all' }}>
                    Endpoint: {d.endpoint}
                  </Text>
                  {d.lastError && (
                    <Text size="xs" c="red">
                      Last error: {d.lastError}
                    </Text>
                  )}
                  {eff === 'retrying' && d.nextAttemptAt && (
                    <Text size="xs" c="dimmed">
                      Next attempt: {new Date(d.nextAttemptAt).toLocaleString()}
                    </Text>
                  )}
                  {eff === 'dead' && (
                    <Text size="xs" c="dimmed">
                      Won&apos;t retry automatically — re-queue after fixing your receiver.
                    </Text>
                  )}
                  <Text size="xs" c="dimmed">
                    Payload:
                  </Text>
                  <Card withBorder p="xs" bg="var(--mantine-color-gray-0)">
                    <Text
                      size="11px"
                      ff="monospace"
                      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                    >
                      {prettyJson(d.payloadJson)}
                    </Text>
                  </Card>
                </Stack>
              )}
            </Card>
          );
        })}
      </Stack>

      <Modal
        opened={confirmClear}
        onClose={confirmClearHandlers.close}
        title="Clear delivery log?"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Remove all delivery log rows for this workspace? This only clears the history — new
            events start a fresh log. This can&apos;t be undone.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="xs" onClick={confirmClearHandlers.close}>
              Cancel
            </Button>
            <Button
              color="red"
              size="xs"
              loading={clearLog.isPending}
              onClick={() => clearLog.mutate({})}
            >
              Clear log
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function prettyJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
